import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert as MuiAlert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popper,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useSecureAuth } from "../../contexts/SecureAuthContext";
import { apiService } from "../../config/api";

// Interfaces
interface ParticipantDetails {
  participantId: string;
}

interface ContactHistory {
  id: string;
  contactDate: string;
  reason?: string;
  messageBody?: string;
  anomalyDate?: string;
  status: string;
}

interface AlertEvent {
  eventId: string;
  alertType: string;
  reason?: string;
  priorityScore: number;
  status: string;
  detectedAt: string;
  anomalyDate: string;
  actionTaken?: string;
}

interface EWMAChartData {
  date: string;
  dayName: string;
  avgScore: number;
  maxScore: number;
  alertCount: number;
  alerts: Array<{
    score: number;
    alertType: string;
    anomalyDate: string;
    detectedAt: string;
  }>;
}

// Component
const ParticipantProfile: React.FC = () => {
  const { participantId, studyId } = useParams<{
    participantId: string;
    studyId: string;
  }>();
  const navigate = useNavigate();
  const { user } = useSecureAuth();

  const [, setParticipantDetails] =
    useState<ParticipantDetails | null>(null);
  const [contactHistory, setContactHistory] = useState<ContactHistory[]>([]);
  const [alertHistory, setAlertHistory] = useState<AlertEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  // Sort and filter state
  const [sortBy, setSortBy] = useState<string>("anomaly_desc");
  const [filterAlertType, setFilterAlertType] = useState<string>("");
  const [filterActionTaken, setFilterActionTaken] = useState<string>("");

  // Popover state for chart tooltip
  const [chartTooltip, setChartTooltip] = useState<{
    anchorEl: HTMLElement | null;
    data: any;
    dataIndex: number;
    mousePos?: { x: number; y: number } | null;
  }>({
    anchorEl: null,
    data: null,
    dataIndex: -1,
    mousePos: null,
  });

  // Hover timers to prevent flicker
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const clearTimers = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const formatSimpleDate = (dateString?: string) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Helper: Get status chip color
  const getStatusColor = (status: string): any => {
    switch (status) {
      case "sent":
      case "resolved":
      case "approved":
        return "success";
      case "pending":
        return "info";
      case "failed":
      case "rejected":
        return "error";
      case "snoozed":
      case "ignored":
        return "warning";
      default:
        return "default";
    }
  };

  const getScoreColor = (score: number): any => {
    if (score >= 0.7) return "error";
    if (score >= 0.4) return "warning";
    return "success";
  };

  const getScoreIntensityColor = (score: number): string => {
    if (score >= 0.7) return "#d32f2f"; // Red - High risk
    if (score >= 0.4) return "#f57c00"; // Orange - Medium risk
    return "#388e3c"; // Green - Low risk
  };

  // Helper: Prepare EWMA chart data for last 30 days
  const getEWMAChartData = useCallback((): EWMAChartData[] => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Group alerts by date
    const alertsByDate: {
      [key: string]: Array<{
        score: number;
        alertType: string;
        anomalyDate: string;
        detectedAt: string;
      }>;
    } = {};

    alertHistory.forEach((alert) => {
      const alertDate = new Date(alert.anomalyDate);
      if (alertDate >= thirtyDaysAgo && alertDate <= now) {
        const dateKey = alertDate.toISOString().split("T")[0]; // YYYY-MM-DD
        if (!alertsByDate[dateKey]) {
          alertsByDate[dateKey] = [];
        }
        alertsByDate[dateKey].push({
          score: alert.priorityScore,
          alertType: alert.alertType,
          anomalyDate: alert.anomalyDate,
          detectedAt: alert.detectedAt,
        });
      }
    });

    // Create sorted array of the last 30 days
    const chartData: EWMAChartData[] = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dateKey = date.toISOString().split("T")[0];
      const dayName = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });

      const alerts = alertsByDate[dateKey] || [];
      const avgScore =
        alerts.length > 0
          ? alerts.reduce((sum, a) => sum + a.score, 0) / alerts.length
          : 0;

      chartData.push({
        date: dateKey,
        dayName,
        avgScore,
        maxScore:
          alerts.length > 0 ? Math.max(...alerts.map((a) => a.score)) : 0,
        alertCount: alerts.length,
        alerts,
      });
    }

    return chartData;
  }, [alertHistory]);

  // Get unique alert types from alertHistory
  const uniqueAlertTypes = Array.from(
    new Set(alertHistory.map((a) => a.alertType))
  ).sort();

  // Get unique action taken values from alertHistory
  const uniqueActionTaken = Array.from(
    new Set(
      alertHistory.map((a) => a.actionTaken).filter((a) => a && a !== "—")
    )
  ).sort();

  // Apply sort and filter to alert history
  const getProcessedAlerts = () => {
    let processed = [...alertHistory];

    // Apply filters
    if (filterAlertType) {
      processed = processed.filter((a) => a.alertType === filterAlertType);
    }
    if (filterActionTaken) {
      processed = processed.filter((a) => a.actionTaken === filterActionTaken);
    }

    // Apply sort
    switch (sortBy) {
      case "score_asc":
        processed.sort((a, b) => a.priorityScore - b.priorityScore);
        break;
      case "score_desc":
        processed.sort((a, b) => b.priorityScore - a.priorityScore);
        break;
      case "detection_asc":
        processed.sort(
          (a, b) =>
            new Date(a.detectedAt).getTime() - new Date(b.detectedAt).getTime()
        );
        break;
      case "detection_desc":
        processed.sort(
          (a, b) =>
            new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
        );
        break;
      case "anomaly_asc":
        processed.sort(
          (a, b) =>
            new Date(a.anomalyDate).getTime() -
            new Date(b.anomalyDate).getTime()
        );
        break;
      case "anomaly_desc":
        processed.sort(
          (a, b) =>
            new Date(b.anomalyDate).getTime() -
            new Date(a.anomalyDate).getTime()
        );
        break;
      default:
        break;
    }

    return processed;
  };

  // Clear filters
  const handleClearFilters = () => {
    setSortBy("anomaly_desc");
    setFilterAlertType("");
    setFilterActionTaken("");
  };

  const chartData = useMemo(() => getEWMAChartData(), [getEWMAChartData]);

  useEffect(() => {
    const fetchData = async () => {
      if (!participantId || !studyId || !user?.uid) {
        setError("Missing required parameters");
        setLoading(false);
        return;
      }

      try {
        const token = await apiService.getAuthToken();

        const url = `${
          process.env.REACT_APP_API_URL || ""
        }/api/anomaly-detection/alerts/participant-profile/${participantId}?studyId=${studyId}`;

        const resp = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const responseData = await resp.json();

        if (resp.ok && responseData?.data) {
          setParticipantDetails(responseData.data.participant);
          setContactHistory(responseData.data.contactHistory || []);
          setAlertHistory(responseData.data.alerts || []);
        } else {
          setError(responseData?.error || "Failed to fetch participant data");
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to fetch participant profile"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [participantId, studyId, user]);

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            variant="text"
          >
            Back
          </Button>
        </Box>
        <MuiAlert severity="error">{error}</MuiAlert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          variant="text"
        >
          Back
        </Button>
      </Box>

      {/* EWMA Risk Score Visualization */}
      {alertHistory.length > 0 && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            EWMA Risk Scores - Last 30 Days
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 3,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: "#d32f2f",
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="caption">High Risk (≥0.7)</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: "#f57c00",
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="caption">Medium Risk (0.4-0.7)</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: "#388e3c",
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="caption">Low Risk (&lt;0.4)</Typography>
              </Box>
            </Box>
          </Box>

          {/* Bar Chart */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 4,
              height: 350,
              justifyContent: "space-between",
              p: 2,
              bgcolor: "#fafafa",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
              width: "100%",
            }}
          >
            {chartData.map((dataPoint, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  height: "100%",
                  flex: 1,
                  minWidth: 0,
                  cursor: dataPoint.alertCount > 0 ? "pointer" : "default",
                  transition: "all 0.15s ease-out",
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                  if (dataPoint.alertCount > 0) {
                    clearTimers();

                    const mouseX = e.clientX;
                    const mouseY = e.clientY;
                    openTimerRef.current = window.setTimeout(() => {
                      setChartTooltip({
                        anchorEl: null,
                        data: dataPoint,
                        dataIndex: idx,
                        mousePos: { x: mouseX, y: mouseY + 12 },
                      });
                    }, 150);
                  }
                }}
                onMouseLeave={() => {
                  clearTimers();
                  closeTimerRef.current = window.setTimeout(() => {
                    setChartTooltip({
                      anchorEl: null,
                      data: null,
                      dataIndex: -1,
                      mousePos: null,
                    });
                  }, 250);
                }}
              >
                {/* Bar */}
                <Box
                  data-bar="true"
                  sx={{
                    width: "clamp(8px, 60%, 18px)",
                    height: `${Math.max(dataPoint.avgScore * 250, 4)}px`,
                    bgcolor:
                      dataPoint.alertCount > 0
                        ? getScoreIntensityColor(dataPoint.maxScore)
                        : "#e0e0e0",
                    borderRadius: "2px 2px 0 0",
                    transition: "all 0.15s ease-out",
                    "&:hover": {
                      opacity: 0.85,
                      boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
                    },
                  }}
                  title={
                    dataPoint.alertCount > 0
                      ? `${dataPoint.dayName}: ${
                          dataPoint.alertCount
                        } alert(s), Avg Score: ${dataPoint.avgScore.toFixed(2)}`
                      : `${dataPoint.dayName}: No alerts`
                  }
                />

                {/* Date Label - Rotated */}
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    fontSize: "0.65rem",
                    transform: "rotate(-45deg)",
                    transformOrigin: "top center",
                    whiteSpace: "nowrap",
                    height: "40px",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                  }}
                >
                  {dataPoint.dayName}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Contact History */}
        <Grid item xs={12}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Contact History
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {contactHistory.length > 0 ? (
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "background.default" }}>
                          <TableCell>Contact Date</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell>Anomaly Date</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {contactHistory.map((contact) => (
                          <TableRow key={contact.id}>
                            <TableCell>
                              {formatDate(contact.contactDate)}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {contact.reason || contact.messageBody || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {formatSimpleDate(contact.anomalyDate)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={contact.status}
                                color={getStatusColor(contact.status)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No contact history available.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Alert History */}
        <Grid item xs={12}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                All Alerts & Anomalies
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {alertHistory.length > 0 ? (
                <Box>
                  {/* Sort and Filter Section */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 3,
                      flexWrap: "wrap",
                      alignItems: "flex-end",
                    }}
                  >
                    <FormControl sx={{ minWidth: 240 }}>
                      <InputLabel>Sort By</InputLabel>
                      <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        label="Sort By"
                        size="small"
                      >
                        <MenuItem value="score_desc">
                          Risk Score (High to Low)
                        </MenuItem>
                        <MenuItem value="score_asc">
                          Risk Score (Low to High)
                        </MenuItem>
                        <MenuItem value="detection_desc">
                          Detection Date (Newest)
                        </MenuItem>
                        <MenuItem value="detection_asc">
                          Detection Date (Oldest)
                        </MenuItem>
                        <MenuItem value="anomaly_desc">
                          Anomaly Date (Newest)
                        </MenuItem>
                        <MenuItem value="anomaly_asc">
                          Anomaly Date (Oldest)
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 240 }}>
                      <InputLabel>Filter by Alert Type</InputLabel>
                      <Select
                        value={filterAlertType}
                        onChange={(e) => setFilterAlertType(e.target.value)}
                        label="Filter by Alert Type"
                        size="small"
                      >
                        <MenuItem value="">
                          <em>All</em>
                        </MenuItem>
                        {uniqueAlertTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {uniqueActionTaken.length > 0 && (
                      <FormControl sx={{ minWidth: 240 }}>
                        <InputLabel>Filter by Action Taken</InputLabel>
                        <Select
                          value={filterActionTaken}
                          onChange={(e) => setFilterActionTaken(e.target.value)}
                          label="Filter by Action Taken"
                          size="small"
                        >
                          <MenuItem value="">
                            <em>All</em>
                          </MenuItem>
                          {uniqueActionTaken.map((action) => (
                            <MenuItem key={action} value={action}>
                              {action}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}{" "}
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={handleClearFilters}
                      size="small"
                      sx={{
                        visibility:
                          sortBy === "anomaly_desc" &&
                          !filterAlertType &&
                          !filterActionTaken
                            ? "hidden"
                            : "visible",
                      }}
                    >
                      Clear Filters
                    </Button>
                  </Box>

                  {/* Alerts Table */}
                  <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <TableContainer>
                      <Table size="small" sx={{ minWidth: 600 }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: "background.default" }}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Alert Type
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Reason
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                              Risk Score
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">
                              Status
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Detected
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Anomaly Date
                            </TableCell>
                            {alertHistory.some((a) => a.actionTaken) && (
                              <TableCell sx={{ fontWeight: 600 }}>
                                Action Taken
                              </TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getProcessedAlerts().map((alert) => (
                            <TableRow
                              key={alert.eventId}
                              sx={{
                                "&:hover": {
                                  bgcolor: "action.hover",
                                },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 500 }}>
                                {alert.alertType}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 300 }}>
                                <Typography variant="body2">
                                  {alert.reason || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={alert.priorityScore.toFixed(2)}
                                  color={getScoreColor(alert.priorityScore)}
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={alert.status}
                                  color={getStatusColor(alert.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatDate(alert.detectedAt)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatSimpleDate(alert.anomalyDate)}
                                </Typography>
                              </TableCell>
                              {alertHistory.some((a) => a.actionTaken) && (
                                <TableCell>
                                  <Typography variant="body2">
                                    {alert.actionTaken || "—"}
                                  </Typography>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {getProcessedAlerts().length === 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      No alerts match the selected filters.
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No alerts available for this participant.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* EWMA Chart Tooltip Popper */}
      <Popper
        open={Boolean(chartTooltip.data)}
        anchorEl={
          chartTooltip.mousePos
            ? {
                getBoundingClientRect: () => ({
                  top: chartTooltip.mousePos!.y,
                  left: chartTooltip.mousePos!.x,
                  bottom: chartTooltip.mousePos!.y,
                  right: chartTooltip.mousePos!.x,
                  x: chartTooltip.mousePos!.x,
                  y: chartTooltip.mousePos!.y,
                  width: 0,
                  height: 0,
                  toJSON: () => ({}),
                }),
              }
            : chartTooltip.anchorEl
        }
        placement="bottom"
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        sx={{ zIndex: 1500 }}
        disablePortal
        onMouseEnter={() => {
          if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
          }
        }}
        onMouseLeave={() => {
          clearTimers();
          closeTimerRef.current = window.setTimeout(() => {
            setChartTooltip({
              anchorEl: null,
              data: null,
              dataIndex: -1,
              mousePos: null,
            });
          }, 200);
        }}
      >
        {chartTooltip.data && (
          <Paper sx={{ p: 2, maxWidth: 350, pointerEvents: "auto" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {chartTooltip.data.dayName}
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Alert Count: <strong>{chartTooltip.data.alertCount}</strong>
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                Avg Score:{" "}
                <strong>{chartTooltip.data.avgScore.toFixed(3)}</strong>
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                Max Score:{" "}
                <strong>{chartTooltip.data.maxScore.toFixed(3)}</strong>
              </Typography>
            </Box>

            <Typography
              variant="caption"
              sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
            >
              Alerts:
            </Typography>

            <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
              {chartTooltip.data.alerts.map((alert, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 0.75,
                    mb: 0.75,
                    bgcolor: "background.default",
                    borderRadius: 0.5,
                    borderLeft: `3px solid ${getScoreIntensityColor(
                      alert.score
                    )}`,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {alert.alertType}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Score: {alert.score.toFixed(3)}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Detected: {formatSimpleDate(alert.detectedAt)}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Anomaly: {formatSimpleDate(alert.anomalyDate)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}
      </Popper>
    </Container>
  );
};

export default ParticipantProfile;
